import React from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { AppHeader } from "../../../../components/layout/AppHeader";
import { ButtonLink } from "../../../../components/ui/button";
import { LandingFooter } from "../landing/LandingFooter";
import { CTASection } from "../landing/CTASection";
import { FadeUp } from "../landing/FadeUp";
import { useLandingScrollToSection } from "../marketing/useLandingScrollToSection";
import type { BlogPostFull, RouteMap, UserDisplay } from "../../types";

type BlogPostPageProps = {
  post: BlogPostFull;
  loggedIn: boolean;
  hasBusiness: boolean;
  routes: RouteMap;
  csrfToken: string;
  page: string;
  userDisplay?: UserDisplay | null;
};

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return null;
  }
};

export const BlogPostPage: React.FC<BlogPostPageProps> = ({
  post,
  loggedIn,
  hasBusiness,
  routes,
  csrfToken,
  page,
  userDisplay,
}) => {
  const scrollToSection = useLandingScrollToSection();

  const dateLabel = formatDate(post.publishedAt);

  return (
    <div className="min-h-screen overflow-x-hidden bg-background text-foreground">
      <AppHeader
        variant="app"
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        csrfToken={csrfToken}
        page={page}
        userDisplay={userDisplay}
      />
      <article className="bg-background pt-20 pb-16 sm:pt-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <FadeUp>
            <a
              href="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-premium-600"
            >
              <ArrowLeft className="h-4 w-4" />
              All articles
            </a>
            <div className="mt-6 flex flex-wrap gap-2">
              {post.tradeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-premium-50 px-2.5 py-0.5 text-xs font-semibold text-premium-700"
                >
                  {tag.replace(/-/g, " ")}
                </span>
              ))}
            </div>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
              {post.title}
            </h1>
            <div className="mt-4 flex items-center gap-3 text-sm text-muted-foreground">
              <span>{post.authorName}</span>
              {dateLabel ? (
                <>
                  <span aria-hidden="true">&middot;</span>
                  <time dateTime={post.publishedAt ?? undefined}>
                    {dateLabel}
                  </time>
                </>
              ) : null}
            </div>
          </FadeUp>

          {post.heroImageUrl ? (
            <div className="mt-10 overflow-hidden rounded-2xl border border-border shadow-md">
              <img
                src={post.heroImageUrl}
                alt={post.heroImageAlt ?? post.title}
                className="h-full w-full object-cover"
                loading="eager"
                decoding="sync"
              />
            </div>
          ) : null}

          <div
            className="markdown-content mt-10"
            dangerouslySetInnerHTML={{ __html: post.bodyHtml }}
          />

          {post.relatedTradeHrefs.length > 0 ? (
            <aside className="mt-12 rounded-2xl border border-border bg-card p-6">
              <h2 className="font-display text-xl font-bold text-foreground">
                See how ToolKit helps your trade
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                Get a website tuned for your specific trade and the services
                your customers actually search for.
              </p>
              <ul className="mt-4 flex flex-wrap gap-3">
                {post.relatedTradeHrefs.map((trade) => (
                  <li key={trade.slug}>
                    <ButtonLink to={trade.href} variant="outline" size="sm">
                      For {trade.label}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </ButtonLink>
                  </li>
                ))}
              </ul>
            </aside>
          ) : null}
        </div>
      </article>
      <CTASection routes={routes} />
      <LandingFooter
        loggedIn={loggedIn}
        hasBusiness={hasBusiness}
        routes={routes}
        scrollToSection={scrollToSection}
      />
    </div>
  );
};
